import { useEffect, useMemo, useState } from 'react';

import {
  KeyedPaginatedResponse,
  MeasurementRangesRequestParams,
  MeasurementRangeSubSensorResponse,
  MeasurementSeriesStatusMap,
  PaginatedMeasurementRequestParams,
  StructuredMeasurements,
  WindowSize,
} from '@restDataAccess';
import {
  BiomassDistributionRequest,
  BiomassDistributionResponse,
  BiomassReportSummary,
  MeasurementEndpoints,
  MeasurementQueryRequest,
  MeasurementRange,
  MeasurementRangeQuery,
  MeasurementScatterPlotRequest,
  MeasurementScatterPlotResponse,
  MeasurementStatisticalAnalysis,
  SitecontrolMeasurement,
  WelfareScoreEntry,
} from '@scaleaq/sitecontrol-lib';
import {
  QueryFunctionContext,
  useInfiniteQuery,
  useQueries,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { AuthContextProps, useAuth } from 'react-oidc-context';

import { ChartAggregationResolution } from '@/components/chart/types.ts';
import { API_NAMES, CACHE_KEYS } from '@/constants';
import {
  assertApiBaseForFetch,
  useAuthApiGetKeyedMultiFetch,
  useAuthApiGetSingleFetch,
  useGetApiUrl,
} from '@/dataAccess/rest/apiHooks';
import { getSequentialStepJitterMs } from '@/utils/refetchJitter.ts';

import { authorizationHeader } from '../authorizationHeader.ts';
import { fetchAuthApiGet } from '../fetchAuthApiGet.ts';
import {
  isTooManyRequestsError,
  rateLimitRetryOptions,
  throwIfTooManyRequests,
  TOO_MANY_REQUESTS_STATUS,
  TooManyRequestsError,
} from '../rateLimit.ts';
import { WelfareProvider } from './measurement.types.ts';
import {
  type ApiDateRange,
  getMeasurementRangeSubSensorCacheKey,
  getWeeklyApiRanges,
  getWelfareScoresWindow,
  paginatedRequestToQueryString,
  requestToQueryString,
  type WelfareScoresWindow,
} from './measurement.utils.ts';

const baseApiRoute = API_NAMES.cloudMeasurementApi;

type UseGetMeasurementRangeSubSensorOptions = {
  enabled?: boolean;
  refetchInterval?: number;
  staleTime?: number;
};

export const useGetMeasurementTypes = (inventoryIds: string[] | undefined) => {
  const routes = useMemo(
    () =>
      inventoryIds?.length
        ? inventoryIds.reduce(
            (acc, id) => ({
              ...acc,
              [id]: MeasurementEndpoints.typeBySensor.replace('{inventoryId}', id),
            }),
            {}
          )
        : { '': MeasurementEndpoints.types },
    [inventoryIds]
  );

  return useAuthApiGetKeyedMultiFetch<string[]>({
    baseApiRoute,
    cacheKeys: [CACHE_KEYS.measurementTypes, inventoryIds?.join(', ')],
    routes,
  });
};

type useGetBiomassDailySummaryProps = {
  days?: number;
  refetchInterval?: number;
  inventoryId: string;
  /** Pass false to skip the request, e.g. for a pen that carries no biomass provider. */
  enabled?: boolean;
};
export const useGetBiomassDailySummary = ({
  inventoryId,
  days = 7,
  refetchInterval,
  enabled = true,
}: useGetBiomassDailySummaryProps) => {
  return useAuthApiGetSingleFetch<BiomassReportSummary>({
    baseApiRoute,
    cacheKeys: [CACHE_KEYS.biomassDailySummary, inventoryId, days],
    route:
      MeasurementEndpoints.biomassDailySummary.replace('{inventoryId}', inventoryId) +
      `?days=${days}`,
    enabled: enabled && !!inventoryId,
    refetchInterval,
  });
};

/**
 * Server-side biomass distribution: bins plus the statistics for the estimated curve in a
 * single request. `binSize` and the returned bin bounds are in kilograms.
 */
export const useGetBiomassDistribution = (
  inventoryId: string,
  request: BiomassDistributionRequest,
  refetchInterval?: number
) => {
  return useAuthApiGetSingleFetch<BiomassDistributionResponse>({
    baseApiRoute,
    cacheKeys: [CACHE_KEYS.biomassDistribution, inventoryId, JSON.stringify(request)],
    route: `${MeasurementEndpoints.biomassDistribution.replace('{inventoryId}', inventoryId)}?${requestToQueryString(request)}`,
    enabled: !!inventoryId,
    refetchInterval,
  });
};

export const useGetBoxPlotData = (
  requests: Record<string, MeasurementQueryRequest>,
  refetchInterval?: number
) => {
  const hasQueryableRequests =
    Object.keys(requests).length > 0 &&
    Object.values(requests).every(request => Boolean(request.inventoryId));

  return useAuthApiGetKeyedMultiFetch<Record<string, MeasurementStatisticalAnalysis>>({
    baseApiRoute,
    cacheKeys: [CACHE_KEYS.statisticalAnalysis, JSON.stringify(requests)],
    enabled: hasQueryableRequests,
    routes: Object.entries(requests).reduce(
      (acc, [key, request]) => ({
        ...acc,
        [key]: `${MeasurementEndpoints.statisticalAnalysis}?${requestToQueryString(request)}`,
      }),
      {} as Record<string, string>
    ),
    refetchInterval,
  });
};

export const useGetScatterPlotData = (
  request: MeasurementScatterPlotRequest,
  refetchInterval?: number
) => {
  return useAuthApiGetSingleFetch<MeasurementScatterPlotResponse | null>({
    baseApiRoute,
    cacheKeys: [CACHE_KEYS.scatterPlotData, JSON.stringify(request)],
    route: `${MeasurementEndpoints.scatterPlotData}?${requestToQueryString(request)}`,
    enabled: Boolean(request.inventoryIdA) && Boolean(request.inventoryIdB),
    refetchInterval,
  });
};

export const useGetMeasurementRangeSubSensor = (
  request: MeasurementRangeQuery,
  { enabled, refetchInterval, staleTime }: UseGetMeasurementRangeSubSensorOptions = {}
) => {
  return useAuthApiGetSingleFetch<MeasurementRangeSubSensorResponse>({
    baseApiRoute,
    cacheKeys: [CACHE_KEYS.measurementRanges, ...getMeasurementRangeSubSensorCacheKey(request)],
    route: `${MeasurementEndpoints.measurementRanges}?${requestToQueryString(request)}`,
    enabled,
    refetchInterval,
    staleTime,
  });
};

export const useGetMeasurementPaginated = (
  queries: PaginatedMeasurementRequestParams[],
  refetchInterval?: number
) => {
  const baseUrl = useGetApiUrl(baseApiRoute);
  const auth = useAuth();
  const { fetchNextPage, hasNextPage, isFetchingNextPage, data, ...result } = useInfiniteQuery<
    KeyedPaginatedResponse<SitecontrolMeasurement>[]
  >({
    ...rateLimitRetryOptions,
    queryKey: [CACHE_KEYS.measurements, baseUrl, ...queries.map(query => JSON.stringify(query))],
    queryFn: ctx => {
      const resolved = assertApiBaseForFetch(baseUrl);
      return getMeasurementPaginated(
        queries,
        `${resolved}${MeasurementEndpoints.measurements}`,
        auth
      )(ctx);
    },
    getNextPageParam: getNextPage,
    initialPageParam: 1,
    refetchInterval,
    enabled: baseUrl != null && hasQueryableInventoryIds(queries),
  });

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const measurements = useMemo<StructuredMeasurements<SitecontrolMeasurement>>(
    () => reducePages(data?.pages.flat() ?? []),
    [data?.pages]
  );

  return {
    ...result,
    data: measurements,
    isRateLimited: isTooManyRequestsError(result.failureReason),
  };
};

/**
 * One query per series rather than a single fan-out, so a series that has arrived stays cached
 * while a throttled sibling keeps retrying. A combined query would re-request every series on
 * each retry, which under rate limiting spends more of the budget the harder it fails.
 */
export const useGetMeasurementRanges = (
  queries: MeasurementRangesRequestParams[],
  refetchInterval?: number
) => {
  const baseUrl = useGetApiUrl(baseApiRoute);
  const auth = useAuth();

  return useQueries({
    queries: queries.map(query => ({
      ...rateLimitRetryOptions,
      queryKey: [CACHE_KEYS.measurementRanges, baseUrl, JSON.stringify(query)],
      queryFn: () => {
        const resolved = assertApiBaseForFetch(baseUrl);
        return getMeasurementRange(
          query,
          `${resolved}${MeasurementEndpoints.measurementRanges}`,
          auth
        );
      },
      refetchInterval,
      enabled: baseUrl != null && Boolean(query.inventoryId),
    })),
    // Close over `queries` so failed/in-flight legs still get a type + sensor key in seriesStatus
    // (response `data` is missing until a leg succeeds).
    combine: results => combineMeasurementRanges(results, queries),
  });
};

/**
 * Sequential sibling of `useGetMeasurementRanges`, for widgets that request one query per
 * inventory rather than a handful of series for one chart. A cursor walks the query list and only
 * advances once the current query has settled, so the queries behind it stay unsent while a
 * rate-limited request retries — the same pacing `useGetWelfareScoresForInventoryIds` applies to
 * the welfare route. Each step after the first waits a random moment before it is sent, so viewers
 * who opened the widget together do not walk the list in lockstep. Query keys match the parallel
 * hook, so both read and fill the same cache.
 */
export const useGetMeasurementRangesSequential = (
  queries: MeasurementRangesRequestParams[],
  refetchInterval?: number
) => {
  const baseUrl = useGetApiUrl(baseApiRoute);
  const auth = useAuth();

  const requestKey = JSON.stringify(queries);
  const [progress, setProgress] = useState<MeasurementRangesProgress>({ requestKey, index: 0 });
  // A changed query list starts over from the first query.
  const index = progress.requestKey === requestKey ? progress.index : 0;
  const isStepReleased = useSequentialStepRelease({ requestKey, index });

  const { settledCount, ...result } = useQueries({
    queries: queries.map((query, queryIndex) => ({
      ...rateLimitRetryOptions,
      queryKey: [CACHE_KEYS.measurementRanges, baseUrl, JSON.stringify(query)],
      queryFn: () => {
        const resolved = assertApiBaseForFetch(baseUrl);
        return getMeasurementRange(
          query,
          `${resolved}${MeasurementEndpoints.measurementRanges}`,
          auth
        );
      },
      refetchInterval,
      enabled:
        baseUrl != null &&
        Boolean(query.inventoryId) &&
        (queryIndex < index || (queryIndex === index && isStepReleased)),
    })),
    combine: results => ({
      ...combineMeasurementRanges(results, queries),
      settledCount: countSettledMeasurementRanges({ results, queries }),
    }),
  });

  useEffect(() => {
    if (settledCount <= index) return;

    setProgress({ requestKey, index: settledCount });
  }, [index, requestKey, settledCount]);

  const isDone = settledCount >= queries.length;

  return {
    ...result,
    // Unlike the parallel hook, a half-walked list is still loading: the queries behind the cursor
    // have not been sent at all, so their series are missing rather than merely late.
    isLoading: queries.length > 0 && !isDone,
    isFetched: isDone,
  };
};

type MeasurementRangesProgress = {
  requestKey: string;
  index: number;
};

/**
 * Fetches welfare indicator/types with individual scores over a number of weeks range.
 */

type useGetWelfareScoresProps = {
  inventoryId: string;
  offset?: { days: number };
  weeks?: number;
  types?: WelfareProvider[];
  refetchInterval?: number;
  enabled?: boolean;
};

type UseGetWelfareScoresForInventoryIdsProps = {
  inventoryIds: string[];
  weeks: number;
  types?: WelfareProvider[];
  enabled?: boolean;
};

/**
 * Weekly welfare scores for several inventories, one request per inventory and week. A cursor walks
 * the inventory × week pairs (every week of one inventory, newest first, before the next) and only advances once
 * the current request has settled, so the rest stay paused while a rate-limited request retries.
 * Each request covers a single week, which keeps it cheap for the API compared to one request
 * spanning the whole window, and each step after the first waits a random moment before it is sent.
 */
export const useGetWelfareScoresForInventoryIds = ({
  weeks,
  types,
  ...rest
}: UseGetWelfareScoresForInventoryIdsProps) =>
  useGetWelfareScoresForInventoryIdsByWeeks({ ...rest, groups: [{ weeks, types }] });

/** Welfare types that share one look-back window. */
export type WelfareScoresWeeksGroup = {
  weeks: number;
  types?: WelfareProvider[];
};

type UseGetWelfareScoresForInventoryIdsByWeeksProps = {
  inventoryIds: string[];
  groups: WelfareScoresWeeksGroup[];
  enabled?: boolean;
};

/**
 * `useGetWelfareScoresForInventoryIds` for types that look back over different numbers of weeks.
 * Each group is requested over its own window, so a type is only fetched for the weeks it needs.
 * The cursor walks inventory, then group, then week (newest first), and every group's entries for
 * an inventory are collected into that inventory's list.
 */
export const useGetWelfareScoresForInventoryIdsByWeeks = ({
  inventoryIds,
  groups,
  enabled = true,
}: UseGetWelfareScoresForInventoryIdsByWeeksProps) => {
  // Newest week first, so each pen's most relevant value arrives before its history.
  const groupRanges = groups.map(({ weeks, types }) => ({
    types,
    weekRanges: getWeeklyApiRanges({ weeks }).reverse(),
  }));
  const stepsPerInventory = groupRanges.reduce((sum, { weekRanges }) => sum + weekRanges.length, 0);
  const requestKey = JSON.stringify([inventoryIds, groupRanges]);
  const [progress, setProgress] = useState<WelfareScoresProgress>({
    requestKey,
    index: 0,
    data: {},
  });
  // A changed request starts over from the first inventory.
  const { index, data }: WelfareScoresProgress =
    progress.requestKey === requestKey ? progress : { requestKey, index: 0, data: {} };

  const inventoryId =
    stepsPerInventory > 0 ? inventoryIds[Math.floor(index / stepsPerInventory)] : undefined;
  const step = getWelfareScoresStep({ groupRanges, stepIndex: index % stepsPerInventory });
  const isStepReleased = useSequentialStepRelease({ requestKey, index });

  const current = useGetWelfareScoresForRange({
    inventoryId: inventoryId ?? '',
    start: step?.weekRange.start ?? '',
    end: step?.weekRange.end ?? '',
    resolution: ChartAggregationResolution.WEEKLY,
    windowSize: WindowSize.SevenDays,
    types: step?.types,
    enabled: enabled && inventoryId != null && step != null && isStepReleased,
  });
  const isSettled = !current.isLoading && (current.data != null || current.isError);

  useEffect(() => {
    if (inventoryId == null || !isSettled) return;

    setProgress({
      requestKey,
      index: index + 1,
      data:
        current.data != null
          ? { ...data, [inventoryId]: [...(data[inventoryId] ?? []), ...current.data] }
          : data,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- advance once per settled request
  }, [index, requestKey, isSettled]);

  // An empty list is done straight away, so callers can report it as empty rather than pending.
  const isDone = index >= inventoryIds.length * stepsPerInventory;

  return {
    data,
    isLoading: enabled && !isDone,
    isError: inventoryIds.length > 0 && isDone && Object.keys(data).length === 0,
    isFetched: isDone,
    isRateLimited: current.isRateLimited,
  };
};

type WelfareScoresProgress = {
  requestKey: string;
  index: number;
  data: Record<string, WelfareScoreEntry[]>;
};

/**
 * Welfare scores for a number of weeks back, optionally shifted by `offset`. Resolves the window
 * with `getWelfareScoresWindow` and fetches it through `useGetWelfareScoresForRange`.
 */
export const useGetWelfareScores = ({
  offset = { days: 0 },
  weeks = 0,
  ...rest
}: useGetWelfareScoresProps) =>
  useGetWelfareScoresForRange({ ...rest, ...getWelfareScoresWindow({ weeks, offset }) });

type UseGetWelfareScoresForRangeProps = WelfareScoresWindow & {
  inventoryId: string;
  types?: WelfareProvider[];
  refetchInterval?: number;
  enabled?: boolean;
};

/**
 * Welfare scores for one inventory over an explicit window. A single-type request reuses an 'ALL'
 * request for the same window when one is in flight or cached, instead of sending its own.
 */
const useGetWelfareScoresForRange = ({
  inventoryId,
  start,
  end,
  resolution,
  windowSize,
  types,
  refetchInterval,
  enabled = true,
}: UseGetWelfareScoresForRangeProps) => {
  const request = {
    inventoryId,
    start,
    end,
    resolution,
    windowSize,
    includeSubSensors: true,
    mergeSeries: true,
    types,
  };

  const baseUrl = useGetApiUrl(baseApiRoute);
  const auth = useAuth();

  // Common cache key parameters excluding 'type'
  const cacheParams = [CACHE_KEYS.welfareScores, inventoryId, windowSize, start, end];

  const isSingleTypeRequest = !!types?.length;

  // 1. Check if an existing 'ALL' query is in-flight or cached, to avoid redundant requests for single types
  // If requests for 1 type, and ALL types run concurrently -> Only fetch ALL types, filter for the single type request
  // If only request for 1 type, and ALL types are cached -> Filter for the single type request from the cached ALL types
  // If only request for 1 type, no ALL types are cached -> Fetch the single type request
  const allScoresRoute = `${MeasurementEndpoints.welfareScores}?${requestToQueryString({
    ...request,
    types: undefined,
  })}`;

  const {
    data: existingAllScoresData,
    isLoading: existingAllScoresIsLoading,
    isError: existingAllScoresIsError,
  } = useQuery<WelfareScoreEntry[]>({
    queryKey: [
      ...cacheParams.filter((key: string | number | undefined) => key != null && key !== undefined),
      'ALL',
      baseUrl,
      allScoresRoute,
    ],
    // Matches the queryFn useAuthApiGetSingleFetch would attach for the same 'ALL' request, so
    // this observer still resolves correctly if it is ever fetched (e.g. no sibling widget has
    // requested 'ALL' types for this inventory/week range yet) instead of throwing "No queryFn".
    queryFn: () =>
      fetchAuthApiGet<WelfareScoreEntry[]>({
        api: baseUrl,
        route: allScoresRoute,
        user: auth.user,
      }),
    enabled: false, // Prevents triggering automatic refetches while observing status
  });

  // Requests for both 1 type and ALL types concurrently cause a race condition. Timeout the 1 type query to ensure the ALL types query win
  const [waitForAllScoresQuery, setWaitForAllScoresQuery] = useState(true);

  useEffect(() => {
    if (!enabled) return;

    const timeout = setTimeout(() => {
      setWaitForAllScoresQuery(false);
    }, 200);
    return () => clearTimeout(timeout);
  }, [enabled]);

  const useExistingAllScores =
    !!existingAllScoresData || existingAllScoresIsLoading || waitForAllScoresQuery;

  // 2. Fetch NEW scores the default way, IF we cannot use existing all scores query (because it is not in-flight or cached)
  const scoresResult = useAuthApiGetSingleFetch<WelfareScoreEntry[]>({
    baseApiRoute,
    cacheKeys: [...cacheParams, types?.join(',') ?? 'ALL'],
    route: `${MeasurementEndpoints.welfareScores}?${requestToQueryString(request)}`,
    enabled: !!inventoryId && enabled && !(isSingleTypeRequest && useExistingAllScores),
    refetchInterval,
  });

  // 3. If single component requested data but 'ALL' data exists globally or locally, return filtered data
  const resolvedData = scoresResult.data || existingAllScoresData;
  const resolvedDataLoading =
    scoresResult.isLoading ||
    existingAllScoresIsLoading ||
    (waitForAllScoresQuery && !existingAllScoresData);
  const resolvedDataError = scoresResult.isError || existingAllScoresIsError;

  return {
    ...scoresResult,
    isLoading: resolvedDataLoading,
    isError: resolvedDataError,
    data:
      isSingleTypeRequest && resolvedData
        ? resolvedData.filter(entry => types.includes(entry.type as WelfareProvider))
        : resolvedData,
  };
};

// ************************
// Help functions
// ************************

/** The group and week a welfare walk requests at `stepIndex` within one inventory. */
const getWelfareScoresStep = ({
  groupRanges,
  stepIndex,
}: {
  groupRanges: { types?: WelfareProvider[]; weekRanges: ApiDateRange[] }[];
  stepIndex: number;
}) => {
  let remaining = stepIndex;

  for (const { types, weekRanges } of groupRanges) {
    if (remaining < weekRanges.length) return { types, weekRange: weekRanges[remaining] };
    remaining -= weekRanges.length;
  }

  return undefined;
};

/**
 * Whether a sequential walk may send the request at `index`. Every step after the first is held
 * back by a random jitter, so viewers who opened the same widget together drift apart instead of
 * requesting in lockstep. A held-back query still serves its cached data, so a step that needs no
 * request settles straight away and the walk only pauses in front of real requests.
 */
const useSequentialStepRelease = ({ requestKey, index }: { requestKey: string; index: number }) => {
  const stepKey = `${index}:${requestKey}`;
  const [releasedStepKey, setReleasedStepKey] = useState<string>();
  const isFirstStep = index === 0;

  useEffect(() => {
    if (isFirstStep) return;

    const timeout = setTimeout(() => setReleasedStepKey(stepKey), getSequentialStepJitterMs());
    return () => clearTimeout(timeout);
  }, [isFirstStep, stepKey]);

  return isFirstStep || releasedStepKey === stepKey;
};
const getMeasurementPaginated =
  (queries: PaginatedMeasurementRequestParams[], url: string, auth: AuthContextProps) =>
  async ({ pageParam }: QueryFunctionContext) => {
    return await Promise.allSettled(
      queries.map(q =>
        fetch(
          `${url}?${paginatedRequestToQueryString({
            ...q,
            page: pageParam as number,
          })}`,
          { headers: authorizationHeader(auth.user) }
        ).then(async response => {
          if (response.ok) {
            return { type: q.type, ...(await response.json()) };
          }
          if (response.status === TOO_MANY_REQUESTS_STATUS) {
            throw new TooManyRequestsError();
          }
          const errorText = await response.text();
          console.error(`Failed to fetch: `, errorText);

          if (response.status === 401) {
            throw new Error(`Unauthorized: Please log in to continue.`);
          }

          throw new Error(`Failed to fetch: ${response.statusText}`);
        })
      )
    ).then(r => {
      throwIfTooManyRequests(r);
      return r
        .filter(
          (
            v: PromiseSettledResult<Awaited<KeyedPaginatedResponse<SitecontrolMeasurement>>>
          ): v is PromiseFulfilledResult<KeyedPaginatedResponse<SitecontrolMeasurement>> =>
            v.status === 'fulfilled'
        )
        .flatMap(v => v.value);
    });
  };

const getMeasurementRange = async (
  query: MeasurementRangesRequestParams,
  url: string,
  auth: AuthContextProps
): Promise<KeyedPaginatedResponse<MeasurementRange>> => {
  const response = await fetch(`${url}?${requestToQueryString(query)}`, {
    headers: authorizationHeader(auth.user),
  });

  if (response.ok) {
    return { type: query.type, ...(await response.json()) };
  }
  if (response.status === TOO_MANY_REQUESTS_STATUS) {
    throw new TooManyRequestsError();
  }
  const errorText = await response.text();
  console.error(`Failed to fetch: `, errorText);

  if (response.status === 401) {
    throw new Error(`Unauthorized: Please log in to continue.`);
  }

  throw new Error(`Failed to fetch: ${response.statusText}`);
};

const combineMeasurementRanges = (
  results: UseQueryResult<KeyedPaginatedResponse<MeasurementRange>>[],
  queries: MeasurementRangesRequestParams[]
) => {
  const loaded = results.flatMap(result => (result.data ? [result.data] : []));

  const seriesStatus = results.reduce<MeasurementSeriesStatusMap>((acc, result, index) => {
    const query = queries[index];
    if (!query?.type || !query.inventoryId) return acc;

    const plotType = result.data?.type ?? query.type;
    const inventoryId = query.inventoryId;

    return {
      ...acc,
      [plotType]: {
        ...acc[plotType],
        [inventoryId]: {
          isError: result.isError,
          isFetched: result.isFetched,
          isLoading: result.isLoading,
          isRateLimited: isTooManyRequestsError(result.failureReason),
        },
      },
    };
  }, {});

  return {
    data: reducePages(loaded),
    // A series that has arrived is worth drawing, so only hold the chart back while none has.
    isLoading: loaded.length === 0 && results.some(result => result.isLoading),
    // One failed series must not blank a chart that still has the others.
    isError: results.length > 0 && results.every(result => result.isError),
    isFetched: results.every(result => result.isFetched),
    isRateLimited: results.some(result => isTooManyRequestsError(result.failureReason)),
    seriesStatus,
  };
};

/**
 * How many queries from the start of the list have finished, so the sequential hook knows how far
 * its cursor may move. A query that can never run (no inventory id) counts as finished, otherwise
 * the cursor would stall behind it and the rest of the list would never be requested.
 */
const countSettledMeasurementRanges = ({
  results,
  queries,
}: {
  results: UseQueryResult<KeyedPaginatedResponse<MeasurementRange>>[];
  queries: MeasurementRangesRequestParams[];
}) => {
  const pendingIndex = results.findIndex(
    (result, index) => Boolean(queries[index]?.inventoryId) && !result.isSuccess && !result.isError
  );

  return pendingIndex === -1 ? results.length : pendingIndex;
};

/**
 * `inventoryId` is typed as required but reaches us empty while a caller waits for the inventory
 * that identifies its series. `paginatedRequestToQueryString` drops empty values, so such a query
 * would go out without the param at all and the API answers 400 rather than an empty result.
 */
const hasQueryableInventoryIds = (queries: PaginatedMeasurementRequestParams[]) =>
  queries.length > 0 && queries.every(query => Boolean(query.inventoryId));

const getNextPage = (lastPage: KeyedPaginatedResponse<SitecontrolMeasurement>[]) =>
  lastPage.reduce<number | undefined>(
    (acc, page) => (getPageSize(page.items) >= page.pageSize ? page.page + 1 : acc),
    undefined
  );

const getPageSize = (items: Record<string, SitecontrolMeasurement[]>) =>
  Object.values(items).reduce((acc, values) => acc + values.length, 0);

const reducePages = <V extends SitecontrolMeasurement | MeasurementRange>(
  pages: KeyedPaginatedResponse<V>[]
): StructuredMeasurements<V> =>
  pages.reduce((acc, page) => {
    const { type, items } = page;

    return {
      ...acc,
      [type]: {
        ...acc[type],
        ...reduceItems(items, acc[type]),
      },
    };
  }, {} as StructuredMeasurements<V>);

const reduceItems = <V extends SitecontrolMeasurement | MeasurementRange>(
  items: Record<string, V[]>,
  oldValues?: Record<string, V[]>
) =>
  Object.entries(items).reduce(
    (typeValues, [inventoryId, values]) => ({
      ...typeValues,
      [inventoryId]: [...(oldValues?.[inventoryId] ?? []), ...values],
    }),
    {} as Record<string, V[]>
  );
